import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT, useSeo, fmtMoney } from "@/lib/i18n";
import { pub, errMsg } from "@/lib/api";
import { PageHead, HaloCard, CtaLink, Notice } from "@/components/bits";
import { useConfig } from "@/components/Layout";
import { TimeZoneTable, VisionGallery } from "@/components/extras";
import { FAQ } from "@/i18n/strings";

export function About() {
  const { t, tl, lp } = useT();
  useSeo(null, tl("nav")[4]);
  return (
    <>
      <PageHead title={t("aboutH1")} lead={t("aboutIntro")} />
      <section className="wrap grid md:grid-cols-[1fr_1.2fr] gap-14 pb-20">
        <div className="grid gap-6">
          <img src="/assets/v2/andreea-heliconia.webp" alt="Andreea smiling beside a heliconia flower" width="640" height="640" className="rounded-t-[240px] rounded-b-3xl w-full max-w-[440px] object-cover aspect-[4/5]" />
          <img src="/assets/v2/andreea-sunset.webp" alt="Portrait of Andreea by the ocean at sunset" width="600" height="750" loading="lazy" className="rounded-3xl w-full max-w-[300px] object-cover aspect-[4/5]" />
          <figure className="max-w-[300px]">
            <video controls muted preload="metadata" poster="/assets/v2/andreea-waterfall.webp" width="360" height="480" className="rounded-2xl w-full" data-testid="about-video"><source src="/assets/andreea-introduction.mp4" type="video/mp4" /></video>
            <figcaption className="text-xs text-[var(--muted)] mt-2">{t("videoIntroLabel")}</figcaption>
          </figure>
        </div>
        <div className="grid gap-12 content-start">
          {["aboutS1", "aboutS2", "aboutS3"].map((k) => { const [h, b] = tl(k); return <div key={k}><div className="keyline mb-4" /><h2 className="text-4xl">{h}</h2><p className="mt-4 text-lg prose-max">{b}</p></div>; })}
          <img src="/assets/v2/andreea-plants.webp" alt="" aria-hidden="true" loading="lazy" className="rounded-3xl w-full object-cover aspect-[16/10]" />
          <CtaLink to={lp("/book")} testid="about-book">{t("aboutCta")}</CtaLink>
        </div>
      </section>
      <VisionGallery />
    </>
  );
}

export function Sessions() {
  const { t, tl, lp, pick, locale } = useT();
  useSeo("sessions");
  const [svcs, setSvcs] = useState([]);
  useEffect(() => { pub.get("/public/services").then((r) => setSvcs(r.data)).catch(() => {}); }, []);
  const th = tl("themes");
  return (
    <>
      <PageHead title={t("sessionsH1")} lead={t("sessionsLead")} />
      <section className="wrap grid md:grid-cols-3 gap-6">
        {[0, 2, 4].map((k) => <HaloCard key={k} className="card p-7"><h3>{th[k]}</h3><p className="text-[var(--muted)] mt-3">{th[k + 1]}</p></HaloCard>)}
      </section>
      <section className="section wrap">
        <TimeZoneTable />
      </section>
      <section className="section wrap">
        <h2>{t("chooseTime")}</h2>
        <Notice testid="sessions-preview-price">{t("previewPrice")}</Notice>
        <div className="grid md:grid-cols-2 gap-6 mt-8 max-w-[860px]">
          {svcs.map((s) => (
            <div key={s.id} className="card p-7" data-testid={`service-${s.id}`}>
              <h3>{pick(s.title_i18n)}</h3>
              <p className="mt-2 text-sm">{s.duration_min} {t("minutes")} · {t("total")} <strong>{fmtMoney(s.price_minor, s.currency, locale)}</strong></p>
              <p className="text-xs text-[var(--muted)] mt-2">{t("availLangs")}: {s.languages.map((l) => l.toUpperCase()).join(" · ")}</p>
              <CtaLink to={lp(`/book?service=${s.id}`)} variant="forest" className="mt-6 w-full" testid={`book-${s.id}`}>{t("chooseTime")}</CtaLink>
            </div>
          ))}
        </div>
        <p className="text-sm text-[var(--muted)] mt-6 prose-max">{t("pricingNote")}</p>
      </section>
      <section className="section bg-[var(--paper)]"><div className="wrap grid md:grid-cols-2 gap-12">
        <div><h2>{t("howH")}</h2><ol className="mt-6 grid gap-4">{tl("steps").map((s, k) => <li key={k} className="flex gap-4"><span className="serif text-3xl text-[var(--gold)]">{k + 1}</span><span>{s}</span></li>)}</ol></div>
        <div><h2>{t("prepH")}</h2><p className="mt-6">{t("prepBody")}</p><p className="notice mt-6">{t("scope")}</p></div>
      </div></section>
    </>
  );
}

function useForm(init) {
  const [v, setV] = useState(init);
  const bind = (k) => ({ value: v[k], onChange: (e) => setV({ ...v, [k]: e.target.value }), name: k, id: k, "data-testid": `field-${k}` });
  return [v, bind, setV];
}

export function Peru() {
  const { t, tl, locale } = useT();
  useSeo("peru");
  const cfg = useConfig();
  const f = tl("peruFields");
  const [v, bind] = useForm({ nickname: "", email: "", phone: "", country: "", preferred_month: "", group_size: "4", trip_length: "", enquiry_type: "private", language: locale, question: "", website: "" });
  const [state, setState] = useState({});
  const submit = async (e) => {
    e.preventDefault(); setState({ busy: true });
    try { await pub.post("/enquiries/peru", { ...v, country: v.country.toUpperCase(), locale }); setState({ ok: true }); }
    catch (er) { setState({ error: errMsg(er, tl("errors")[5]) }); }
  };
  const c = tl("peruCards");
  return (
    <>
      <PageHead title={t("peruH1")} lead={t("peruIntro")} dark />
      <section className="wrap grid md:grid-cols-2 gap-6 -mt-2 pt-12">
        {[0, 2].map((k) => <HaloCard key={k} className="card p-7"><h3>{c[k]}</h3><p className="text-[var(--muted)] mt-3">{c[k + 1]}</p>{k === 0 && <p className="text-sm mt-4 font-semibold">{t("peruNoDates")}</p>}</HaloCard>)}
      </section>
      <section className="section wrap grid md:grid-cols-[1fr_1.1fr] gap-12">
        <div className="grid gap-6 content-start"><figure><img src="/assets/v2/andreea-garden.webp" alt="Andreea in a tropical garden" loading="lazy" className="rounded-3xl w-full object-cover aspect-square" /></figure>
          <p>{t("peruCulture")}</p><p>{t("peruPractical")}</p><p className="notice">{t("peruScope")}</p></div>
        {state.ok ? <div className="card p-8 self-start" role="status" data-testid="peru-success"><h2 className="text-3xl">{t("peruSuccess")}</h2></div> : (
          <form onSubmit={submit} className="card p-8 grid gap-4 self-start" data-testid="peru-form">
            {!cfg.forms_enabled && <Notice>{t("formsDisabled")}</Notice>}
            <p className="help">{t("peruHint")}</p>
            <label className="field">{f[0]}<input required minLength={2} maxLength={40} {...bind("nickname")} /></label>
            <label className="field">{f[1]}<input required type="email" {...bind("email")} /></label>
            <label className="field">{f[8]}<input maxLength={30} {...bind("phone")} /></label>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="field">{f[2]} (ISO, e.g. RO)<input required minLength={2} maxLength={2} {...bind("country")} /></label>
              <label className="field">{f[3]}<input required type="month" {...bind("preferred_month")} /></label>
              <label className="field">{f[4]}<select {...bind("group_size")}><option value="4">4</option><option value="5">5</option><option value="other">{f[12]}</option></select></label>
              <label className="field">{f[9]}<input maxLength={40} {...bind("trip_length")} /></label>
              <label className="field">{f[5]}<select {...bind("enquiry_type")}><option value="announced">{f[10]}</option><option value="private">{f[11]}</option></select></label>
              <label className="field">{f[6]}<select {...bind("language")}>{["en", "ro", "es", "fr", "it"].map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}</select></label>
            </div>
            <label className="field">{f[7]}<textarea maxLength={1500} {...bind("question")} /></label>
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...bind("website")} />
            <p className="help"><Link to="../privacy" relative="path" className="underline">{t("privacyNotice")}</Link></p>
            {state.error && <p className="error-text" role="alert">{state.error}</p>}
            <button className="btn btn-forest" disabled={state.busy || !cfg.forms_enabled} data-testid="peru-submit">{t("peruCta")}</button>
          </form>)}
      </section>
    </>
  );
}

export function Club() {
  const { t, tl, lp } = useT();
  useSeo(null, "Aya Club");
  const tiers = tl("tiers");
  const ranges = ["0–299", "300–799", "800+"];
  return (
    <>
      <PageHead title={t("clubH1")} lead={t("clubBody")} />
      <section className="wrap grid md:grid-cols-3 gap-6">
        {[0, 2, 4].map((k, n) => <HaloCard key={k} className="card p-7" testid={`tier-${n}`}><p className="eyebrow">{ranges[n]} pts</p><h3>{tiers[k]}</h3><p className="text-[var(--muted)] mt-3">{tiers[k + 1]}</p></HaloCard>)}
      </section>
      <section className="section wrap prose-max"><h2>{tl("progress")[4]}</h2><p className="eyebrow mt-4">{t("proposedTag")}</p><p className="mt-2">{t("clubRules")}</p><p className="mt-6 text-[var(--muted)]">{t("pointsNote")}</p>
        <CtaLink to={lp("/account/club")} className="mt-8" testid="club-cta">{t("clubCta")}</CtaLink></section>
    </>
  );
}

export function Contact() {
  const { t, tl, locale } = useT();
  useSeo(null, tl("nav")[10]);
  const cfg = useConfig();
  const f = tl("contactFields"), subj = tl("subjects");
  const [v, bind] = useForm({ nickname: "", email: "", subject: "general", message: "", website: "" });
  const [state, setState] = useState({});
  const submit = async (e) => {
    e.preventDefault(); setState({ busy: true });
    try { await pub.post("/contact", { ...v, locale }); setState({ ok: true }); } catch (er) { setState({ error: errMsg(er, tl("errors")[5]) }); }
  };
  return (
    <>
      <PageHead title={t("contactH1")} lead={t("contactIntro")} />
      <section className="wrap pb-20 max-w-[720px]">
        {state.ok ? <p className="card p-8 serif text-2xl" role="status" data-testid="contact-success">{t("contactOk")}</p> : (
          <form onSubmit={submit} className="card p-8 grid gap-4" data-testid="contact-form">
            {!cfg.forms_enabled && <Notice>{t("formsDisabled")}</Notice>}
            <label className="field">{f[0]}<input required minLength={2} maxLength={40} {...bind("nickname")} /></label>
            <label className="field">{f[1]}<input required type="email" {...bind("email")} /></label>
            <label className="field">{f[2]}<select {...bind("subject")}>{["session", "order", "peru", "booking", "general"].map((k, n) => <option key={k} value={k}>{subj[n]}</option>)}</select></label>
            <label className="field">{f[3]}<textarea required minLength={5} maxLength={1500} {...bind("message")} /></label>
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...bind("website")} />
            <p className="help">{cfg.business_hours_note?.[locale]}</p>
            {state.error && <p className="error-text" role="alert">{state.error}</p>}
            <button className="btn btn-forest" disabled={state.busy || !cfg.forms_enabled} data-testid="contact-submit">{f[4]}</button>
          </form>)}
      </section>
    </>
  );
}

export function Faq() {
  const { t, px, tl } = useT();
  useSeo(null, tl("nav")[11]);
  return (
    <>
      <PageHead title={t("faqH1")} />
      <section className="wrap pb-20 grid gap-3 max-w-[860px]">
        {FAQ.map((f, k) => { const [q, a] = px(f, "FAQ", k).split("|"); return (
          <details key={k} className="card p-6 group" data-testid={`faq-${k}`}><summary className="cursor-pointer serif text-2xl list-none flex justify-between gap-4">{q}<span aria-hidden="true" className="text-[var(--gold)] group-open:rotate-45 transition-transform">+</span></summary><p className="mt-4 text-[var(--muted)]">{a}</p></details>); })}
      </section>
    </>
  );
}

export function NotFound() {
  const { t, tl, lp } = useT();
  return <section className="wrap section"><h1>{t("notFound")}</h1><CtaLink to={lp("")} className="mt-8">{tl("controls")[6]}</CtaLink></section>;
}
